/* V-NEXUS — Cart & AOV
   1. <vn-progress>  free-shipping bar: animates from its previous value to the new one each
                     time Dawn re-renders the cart.
   2. [data-vn-add]  Complete-the-look Add buttons: apply the card's discount code (kept with any
                     code already in the cart), add the size, then re-render the cart drawer
                     (and open it) or the cart page using Dawn's own section lists. */
(() => {
  /* ---------- 1. Progress bar ---------- */
  const lastValue = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VnProgress extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.vn-fs__fill');
      const to = Math.max(0, Math.min(100, Number(this.dataset.value) || 0));
      const key = this.closest('cart-drawer') ? 'drawer' : 'page';
      const from = lastValue.has(key) ? lastValue.get(key) : 0;
      lastValue.set(key, to);
      if (!fill || reduceMotion.matches || from === to) return;
      fill.style.setProperty('--vn-p', from + '%');
      fill.getBoundingClientRect();
      requestAnimationFrame(() => fill.style.setProperty('--vn-p', to + '%'));
      if (to >= 100 && from < 100) this.closest('.vn-fs')?.classList.add('is-celebrate');
    }
  }
  if (!customElements.get('vn-progress')) customElements.define('vn-progress', VnProgress);

  /* ---------- 2. Complete the look ---------- */
  const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/; // only plain discount codes are ever sent
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const post = (url, body) => fetch(url, { method: 'POST', credentials: 'same-origin', headers: json, body: JSON.stringify(body) });

  const applyCode = async (code) => {
    if (!code || !CODE_PATTERN.test(code)) return;
    const cart = await (await fetch(window.routes.cart_url + '.js', { credentials: 'same-origin' })).json();
    const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== code.toLowerCase());
    await post(window.routes.cart_update_url + '.js', { discount: codes.concat(code).join(',') });
  };

  const swap = (html, selector) => {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  };

  const add = async (button) => {
    const card = button.closest('[data-vn-card]');
    const variant = card && card.querySelector('[data-vn-variant]');
    if (!variant || !/^\d+$/.test(variant.value)) return;

    const drawer = document.querySelector('cart-drawer');
    const page = button.closest('cart-items:not(cart-drawer-items)') || (!button.closest('cart-drawer') && document.querySelector('cart-items:not(cart-drawer-items)'));
    const target = page || drawer;
    const sections = target && target.getSectionsToRender ? target.getSectionsToRender() : [];

    button.disabled = true;
    button.classList.add('is-loading');
    try {
      await applyCode(card.dataset.vnCode);
      const res = await post(window.routes.cart_add_url + '.js', {
        items: [{ id: Number(variant.value), quantity: 1 }],
        sections: sections.map((s) => s.section || s.id),
        sections_url: window.location.pathname,
      });
      const state = await res.json();
      if (!res.ok || state.status) throw new Error(state.description || 'Error');

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vn-complete-look', cartData: state });

      if (page) {
        sections.forEach((s) => {
          const host = document.getElementById(s.id);
          const html = state.sections && state.sections[s.section || s.id];
          if (!host || !html) return;
          const next = swap(html, s.selector);
          if (next !== null) (host.querySelector(s.selector) || host).innerHTML = next;
        });
      } else if (drawer && drawer.renderContents) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(Object.assign({}, state.items ? state.items[0] : {}, { sections: state.sections }));
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      const label = button.querySelector('.vn-up__add-label');
      if (label) {
        const text = label.textContent;
        label.textContent = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
        setTimeout(() => { label.textContent = text; }, 2500);
      }
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-vn-add]');
    if (!button) return;
    event.preventDefault();
    add(button);
  });
})();
